import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { ArrowRight, CheckCircle2, Sparkles, GraduationCap, MessageSquare, FileText, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

const Home = () => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const scrollToProducts = () => {
    document.getElementById('products')?.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToContact = () => {
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Please enter a valid email address');
      return;
    }
    setIsSubmitting(true);
    setTimeout(() => {
      toast.success("Thank you for subscribing! We'll keep you updated.");
      setEmail('');
      setIsSubmitting(false);
    }, 1000);
  };

  const products = [
    {
      name: 'ExamPilot',
      status: 'live',
      icon: GraduationCap,
      tagline: 'AI-Powered Exam Preparation Assistant',
      website: 'https://exampilot.live',
      description: 'ExamPilot is an AI-driven study and exam preparation platform that helps students train for their target scores with precision and confidence. It adapts to each learner\'s goals, materials, and progress, providing structured guidance from revision to exam day.',
      features: [
        'Personalised exam training based on your target score',
        'Upload your own materials (notes, textbooks, past papers)',
        'Adaptive practice and mock exams that evolve with your progress',
        'Clear, source-based answers - no generic explanations',
        'Exam-ready feedback to highlight strengths and areas for improvement'
      ],
      gradient: 'from-teal-50 to-cyan-50',
      image: 'https://images.pexels.com/photos/4769486/pexels-photo-4769486.jpeg?auto=compress&cs=tinysrgb&w=800'
    },
    {
      name: 'HelloAI',
      status: 'live',
      icon: MessageSquare,
      tagline: 'AI Receptionist & Communication Assistant',
      website: 'https://gethelloai.app',
      description: 'HelloAI is an AI-powered receptionist designed to help businesses capture leads, manage enquiries, and respond to customers automatically - ensuring no opportunity is missed, even outside business hours.',
      features: [
        '24/7 AI receptionist that never misses a call',
        'Human-like conversations with natural, professional responses',
        'Handles customer enquiries instantly',
        'Appointment booking and call routing',
        'Designed to improve lead capture and customer experience'
      ],
      gradient: 'from-blue-50 to-indigo-50',
      image: 'https://images.pexels.com/photos/7794003/pexels-photo-7794003.jpeg?auto=compress&cs=tinysrgb&w=800'
    },
    {
      name: 'AI Lawyer',
      status: 'coming-soon',
      icon: FileText,
      tagline: 'AI Legal Guidance Assistant',
      website: null,
      description: 'AI Lawyer is an upcoming AI-powered legal assistant designed to help users understand legal issues, explore their options, and navigate legal processes with greater clarity and confidence. It provides accessible, easy-to-understand guidance for everyday legal questions - helping users take informed next steps.',
      features: [
        'AI-driven legal question answering',
        'Plain-English explanations of legal concepts',
        'Guidance on legal processes and options',
        'User-friendly, accessible interface',
        'Designed to assist - not replace - professional legal advice'
      ],
      gradient: 'from-slate-50 to-zinc-50',
      image: 'https://images.pexels.com/photos/32976/pexels-photo.jpg?auto=compress&cs=tinysrgb&w=800'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 w-full bg-white/80 backdrop-blur-md z-50 border-b border-gray-100">
        <nav className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-teal-600" />
            <span className="text-xl font-semibold text-gray-900">SynthiqLabs</span>
          </div>
          <div className="hidden md:flex items-center gap-8">
            <button onClick={scrollToProducts} className="text-gray-600 hover:text-teal-600 transition-colors duration-200">
              Products
            </button>
            <button onClick={() => document.getElementById('about')?.scrollIntoView({ behavior: 'smooth' })} className="text-gray-600 hover:text-teal-600 transition-colors duration-200">
              About
            </button>
            <Button onClick={scrollToContact} variant="default" className="bg-teal-600 hover:bg-teal-700 text-white">
              Get Updates
            </Button>
          </div>
        </nav>
      </header>

      {/* Hero Section */}
      <section className="relative pt-32 pb-24 px-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-teal-50 via-cyan-50 to-blue-50 opacity-50"></div>
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle at 20% 50%, rgba(20, 184, 166, 0.05) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(59, 130, 246, 0.05) 0%, transparent 50%)'
        }}></div>
        
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="space-y-4">
                <h1 className="text-5xl md:text-6xl font-light text-gray-900 leading-tight">
                  SynthiqLabs - <span className="font-normal">AI Products Built to Simplify Complexity</span>
                </h1>
                <p className="text-xl text-gray-600 leading-relaxed">
                  We design and build intelligent AI tools that help people learn faster, communicate better, and navigate complex challenges with confidence.
                </p>
              </div>
              <div className="flex flex-wrap gap-4">
                <Button onClick={scrollToProducts} size="lg" className="bg-teal-600 hover:bg-teal-700 text-white px-8 group">
                  View Products
                  <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform duration-200" />
                </Button>
                <Button onClick={scrollToContact} size="lg" variant="outline" className="border-teal-600 text-teal-600 hover:bg-teal-50 px-8">
                  Get Updates
                </Button>
              </div>
            </div>
            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://images.unsplash.com/photo-1674027444485-cec3da58eef4?w=800&q=80" 
                  alt="AI Technology" 
                  className="w-full h-auto object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-teal-600/20 to-transparent"></div>
              </div>
              <div className="absolute -top-4 -right-4 w-24 h-24 bg-teal-100 rounded-full blur-3xl animate-pulse"></div>
              <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-cyan-100 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 px-6 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8 text-center">
            <div className="space-y-2">
              <div className="text-4xl font-light text-teal-600">2</div>
              <div className="text-gray-600">Products Launched</div>
            </div>
            <div className="space-y-2">
              <div className="text-4xl font-light text-teal-600">3</div>
              <div className="text-gray-600">AI Solutions in Portfolio</div>
            </div>
            <div className="space-y-2">
              <div className="text-4xl font-light text-teal-600">&infin;</div>
              <div className="text-gray-600">More Products in Progress</div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-24 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="space-y-8 text-center">
            <h2 className="text-4xl font-light text-gray-900">About Us</h2>
            <div className="space-y-6 text-lg text-gray-600 leading-relaxed">
              <p>
                We build practical AI products designed to solve real-world problems through simplicity, clarity, and intelligent automation.
              </p>
              <p>
                Our focus is on creating AI tools that help people learn faster, communicate better, and navigate complex processes with confidence. Rather than overloading users with features, we prioritise usability, accuracy, and meaningful outcomes.
              </p>
              <p>
                Each product is designed with a clear purpose: to save time, reduce friction, and make advanced technology accessible to everyone - whether you're a student, a business owner, or someone seeking guidance.
              </p>
              <p>
                We're continuously building and refining AI solutions that turn complexity into clarity.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="py-24 px-6 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-4xl font-light text-gray-900">Our Products</h2>
            <p className="text-xl text-gray-600">AI-powered solutions designed for real-world impact</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {products.map((product, index) => (
              <Card key={index} className="relative overflow-hidden border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                {product.status === 'live' && (
                  <Badge className="absolute top-4 right-4 bg-emerald-100 text-emerald-700 border-emerald-200">
                    Live
                  </Badge>
                )}
                {product.status === 'coming-soon' && (
                  <Badge className="absolute top-4 right-4 bg-gray-100 text-gray-700 border-gray-200">
                    Coming Soon
                  </Badge>
                )}
                
                <div className={`h-48 bg-gradient-to-br ${product.gradient} flex items-center justify-center relative overflow-hidden`}>
                  <img 
                    src={product.image} 
                    alt={product.name} 
                    className="absolute inset-0 w-full h-full object-cover opacity-60"
                  />
                  <product.icon className="w-16 h-16 text-teal-600 relative z-10" strokeWidth={1.5} />
                </div>

                <CardHeader>
                  <CardTitle className="text-2xl text-gray-900">{product.name}</CardTitle>
                  <CardDescription className="text-base text-gray-600">{product.tagline}</CardDescription>
                </CardHeader>

                <CardContent className="space-y-6">
                  <p className="text-gray-600 leading-relaxed">
                    {product.description}
                  </p>

                  <div className="space-y-3">
                    <div className="font-medium text-gray-900">
                      {product.status === 'coming-soon' ? 'Planned Features:' : 'Key Features:'}
                    </div>
                    <ul className="space-y-2">
                      {product.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm text-gray-600">
                          <CheckCircle2 className="w-4 h-4 text-teal-600 mt-0.5 flex-shrink-0" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {product.status === 'live' ? (
                    <Button 
                      className="w-full bg-teal-600 hover:bg-teal-700 text-white group" 
                      onClick={() => window.open(product.website, '_blank')}
                    >
                      Visit Website
                      <ExternalLink className="ml-2 w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-200" />
                    </Button>
                  ) : (
                    <Button className="w-full" disabled variant="secondary">
                      Coming Soon
                    </Button>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 px-6 bg-gradient-to-br from-teal-600 to-cyan-700 text-white">
        <div className="max-w-4xl mx-auto text-center space-y-8">
          <div className="space-y-4">
            <h2 className="text-4xl font-light">Stay Updated</h2>
            <p className="text-xl text-teal-50">
              Get notified when we launch new AI products and features
            </p>
          </div>
          
          <form onSubmit={handleNewsletterSubmit} className="max-w-md mx-auto">
            <div className="flex gap-3">
              <Input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-white text-gray-900 border-0 flex-1"
                required
              />
              <Button 
                type="submit" 
                disabled={isSubmitting}
                className="bg-white text-teal-600 hover:bg-gray-50 px-8"
              >
                {isSubmitting ? 'Subscribing...' : 'Subscribe'}
              </Button>
            </div>
          </form>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 bg-gray-900 text-gray-400">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-teal-500" />
                <span className="text-lg font-semibold text-white">SynthiqLabs</span>
              </div>
              <p className="text-sm">
                Building AI products that simplify complexity and empower people.
              </p>
            </div>

            <div className="space-y-3">
              <div className="text-white font-medium">Products</div>
              <ul className="space-y-2 text-sm">
                <li><a href="https://exampilot.live" target="_blank" rel="noopener noreferrer" className="hover:text-teal-500 transition-colors duration-200">ExamPilot</a></li>
                <li><a href="https://gethelloai.app" target="_blank" rel="noopener noreferrer" className="hover:text-teal-500 transition-colors duration-200">HelloAI</a></li>
                <li><span className="text-gray-500">AI Lawyer (Coming Soon)</span></li>
              </ul>
            </div>

            <div className="space-y-3">
              <div className="text-white font-medium">Company</div>
              <ul className="space-y-2 text-sm">
                <li><button onClick={() => document.getElementById('about')?.scrollIntoView({ behavior: 'smooth' })} className="hover:text-teal-500 transition-colors duration-200">About</button></li>
                <li><button onClick={scrollToProducts} className="hover:text-teal-500 transition-colors duration-200">Products</button></li>
              </ul>
            </div>

            <div className="space-y-3">
              <div className="text-white font-medium">Contact</div>
              <ul className="space-y-2 text-sm">
                <li><a href="mailto:hello@synthiqlabs.live" className="hover:text-teal-500 transition-colors duration-200">hello@synthiqlabs.live</a></li>
              </ul>
            </div>
          </div>

          <div className="pt-8 border-t border-gray-800 text-sm text-center">
            <p>&copy; {new Date().getFullYear()} SynthiqLabs. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
